import {
  BellOutlined,
  BgColorsOutlined,
  CheckOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { MenuDataItem, ProLayoutProps } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig, RuntimeAntdConfig } from '@umijs/max';
import { history, Link, useAntdConfigSetter, useModel } from '@umijs/max';
import { Input, Popover, Space, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { AvatarDropdown, AvatarName, Footer } from '@/components';
import {
  LOGIN_PATH,
  REDIRECT_QUERY_KEY,
  SELECT_CONTEXT_PATH,
  SESSION_EXPIRED_MESSAGE,
} from '@/constants/auth';
import { DEFAULT_THEME_COLOR, THEME_COLOR_PRESETS } from '@/constants/theme';
import '@/assets/global.css';
import { useAuthStore } from '@/stores/auth';
import { useRouteStore } from '@/stores/route';
import type { CurrentUser } from '@/types/auth';
import type { AppRouteMap, AppRouteNode } from '@/types/route';
import {
  getStoredThemeColor,
  resolveThemeColor,
  setStoredThemeColor,
} from '@/utils/theme';
import '@ant-design/v5-patch-for-react-19';
import defaultSettings from '../config/defaultSettings';

export type InitialAppState = {
  settings?: Partial<ProLayoutProps>;
  currentUser?: CurrentUser;
  routeTree?: AppRouteNode[];
  routeMap?: AppRouteMap;
  menuData?: MenuDataItem[];
  firstLeafPath?: string;
  loading?: boolean;
  bootstrap?: () => Promise<InitialAppState>;
};

const isPublicPath = (pathname: string) =>
  pathname.startsWith(LOGIN_PATH) || pathname.startsWith(SELECT_CONTEXT_PATH);

const THEME_COLOR_CHANGED_EVENT = 'zhiht:theme-color-changed';

const syncBrandThemeVars = (themeColor?: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.style.setProperty(
    '--zhiht-primary',
    resolveThemeColor(themeColor),
  );
};

const buildLayoutSettings = (themeColor?: string): Partial<ProLayoutProps> => {
  const colorPrimary = resolveThemeColor(themeColor);
  const settings = defaultSettings as Partial<ProLayoutProps>;

  return {
    ...settings,
    colorPrimary,
    token: {
      ...settings.token,
      sider: {
        ...settings.token?.sider,
        colorTextMenuSelected: colorPrimary,
      },
    },
  };
};

const buildAntdThemeConfig = (themeColor?: string) => {
  const colorPrimary = resolveThemeColor(themeColor);

  return {
    cssVar: true,
    token: {
      fontFamily:
        'sohne-var, AlibabaSans, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontFamilyCode:
        'SourceCodePro, "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace',
      borderRadius: 5,
      controlHeight: 32,
      fontSize: 14,
      colorPrimary,
      colorInfo: '#2874ad',
      colorSuccess: '#15be53',
      colorWarning: '#9b6829',
      colorText: '#273951',
      colorTextHeading: '#061b31',
      colorTextSecondary: '#64748d',
      colorTextTertiary: '#8aa0b8',
      colorBgLayout: '#f6f9fc',
      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBorder: '#e5edf5',
      colorBorderSecondary: '#edf3fa',
      boxShadow:
        'rgba(50,50,93,0.25) 0px 30px 45px -30px, rgba(0,0,0,0.1) 0px 18px 36px -18px',
      boxShadowSecondary: 'rgba(23,23,23,0.08) 0px 15px 35px 0px',
    },
    components: {
      Button: {
        controlHeight: 32,
        paddingInline: 16,
        borderRadius: 4,
        fontWeight: 400,
      },
      Input: {
        controlHeight: 32,
        borderRadius: 4,
      },
      Select: {
        controlHeight: 32,
        borderRadius: 4,
      },
      DatePicker: {
        controlHeight: 32,
        borderRadius: 4,
      },
      Card: {
        borderRadiusLG: 6,
        boxShadowTertiary:
          'rgba(50,50,93,0.18) 0px 24px 42px -28px, rgba(0,0,0,0.08) 0px 14px 30px -18px',
      },
      Table: {
        headerBg: '#f6f9fc',
        headerColor: '#273951',
        rowHoverBg: 'rgba(83,58,253,0.04)',
        borderColor: '#e5edf5',
      },
      Menu: {
        itemBorderRadius: 4,
        itemSelectedBg: 'rgba(83,58,253,0.08)',
        itemHoverBg: 'rgba(83,58,253,0.05)',
      },
      Tag: {
        borderRadiusSM: 4,
      },
    },
  };
};

const mergeThemeIntoSettings = (
  previousSettings: Partial<ProLayoutProps> | undefined,
  themeColor?: string,
): Partial<ProLayoutProps> => {
  const nextSettings = buildLayoutSettings(
    themeColor,
  ) as Partial<ProLayoutProps> & {
    token?: Record<string, unknown>;
  };
  const previousSettingsWithToken = previousSettings as
    | (Partial<ProLayoutProps> & {
        token?: Record<string, unknown>;
      })
    | undefined;
  const previousToken = previousSettingsWithToken?.token;
  const previousSiderToken = previousToken?.sider as
    | Record<string, unknown>
    | undefined;
  const nextSiderToken = nextSettings.token?.sider as
    | Record<string, unknown>
    | undefined;

  return {
    ...nextSettings,
    ...previousSettings,
    colorPrimary: resolveThemeColor(themeColor),
    token: {
      ...nextSettings.token,
      ...previousToken,
      sider: {
        ...nextSiderToken,
        ...previousSiderToken,
        colorTextMenuSelected: resolveThemeColor(themeColor),
      },
    },
  };
};

// 把 Zustand 中已经准备好的运行时数据整理成 Umi initialState，
// 这样 layout、access、运行时路由都能共享同一份上下文。
const buildInitialStateSnapshot = (): Omit<InitialAppState, 'bootstrap'> => {
  const authState = useAuthStore.getState();
  const routeState = useRouteStore.getState();
  const themeColor = getStoredThemeColor();
  syncBrandThemeVars(themeColor);

  return {
    settings: buildLayoutSettings(themeColor),
    currentUser: authState.currentUser,
    routeTree: routeState.routeTree,
    routeMap: routeState.routeMap,
    menuData: routeState.menuData,
    firstLeafPath: routeState.firstLeafPath,
    loading: false,
  };
};

const redirectToLogin = (reason?: string) => {
  const currentPath = history.location.pathname;
  if (currentPath.startsWith(LOGIN_PATH)) {
    return;
  }

  const searchParams = new URLSearchParams();
  searchParams.set(
    REDIRECT_QUERY_KEY,
    `${history.location.pathname}${history.location.search}`,
  );

  if (reason) {
    searchParams.set('reason', reason);
  }

  history.replace({
    pathname: LOGIN_PATH,
    search: searchParams.toString(),
  });
};

const bootstrapApplication = async (): Promise<InitialAppState> => {
  // 应用启动先从本地恢复登录态，再决定是否拉用户信息和菜单。
  useAuthStore.getState().hydrate();
  const accessToken = useAuthStore.getState().accessToken;

  if (!accessToken) {
    useRouteStore.getState().reset();
    return {
      ...buildInitialStateSnapshot(),
      bootstrap: bootstrapApplication,
    };
  }

  try {
    await useAuthStore.getState().bootstrap();
    await useRouteStore.getState().bootstrap();

    return {
      ...buildInitialStateSnapshot(),
      bootstrap: bootstrapApplication,
    };
  } catch (_error) {
    useAuthStore.getState().clearSessionState();
    useRouteStore.getState().reset();
    redirectToLogin(SESSION_EXPIRED_MESSAGE);

    return {
      ...buildInitialStateSnapshot(),
      bootstrap: bootstrapApplication,
    };
  }
};

export async function getInitialState(): Promise<InitialAppState> {
  const snapshot = await bootstrapApplication();

  if (!snapshot.currentUser && !isPublicPath(history.location.pathname)) {
    redirectToLogin();
  }

  return snapshot;
}

const ThemeColorPicker = () => {
  const antdConfigSetter = useAntdConfigSetter();
  const { setInitialState } = useModel('@@initialState');
  const [open, setOpen] = useState(false);
  const [currentColor, setCurrentColor] = useState(getStoredThemeColor());

  const applyThemeColor = (color: string) => {
    const nextColor = resolveThemeColor(color);

    syncBrandThemeVars(nextColor);
    setCurrentColor(nextColor);
    setStoredThemeColor(nextColor);
    setInitialState?.((previousState) => ({
      ...(previousState || {}),
      settings: mergeThemeIntoSettings(previousState?.settings, nextColor),
    }));
    antdConfigSetter((previousConfig) => ({
      ...previousConfig,
      theme: buildAntdThemeConfig(nextColor),
    }));
    window.dispatchEvent(
      new CustomEvent(THEME_COLOR_CHANGED_EVENT, {
        detail: nextColor,
      }),
    );
    setOpen(false);
  };

  useEffect(() => {
    const syncColor = (event?: Event) => {
      if (event instanceof CustomEvent && event.detail) {
        setCurrentColor(resolveThemeColor(String(event.detail)));
        return;
      }

      setCurrentColor(getStoredThemeColor());
    };

    syncColor();
    window.addEventListener('storage', syncColor);
    window.addEventListener(
      THEME_COLOR_CHANGED_EVENT,
      syncColor as EventListener,
    );

    return () => {
      window.removeEventListener('storage', syncColor);
      window.removeEventListener(
        THEME_COLOR_CHANGED_EVENT,
        syncColor as EventListener,
      );
    };
  }, []);

  const palette = (
    <div
      style={{
        padding: '12px 8px',
        minWidth: 220,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 16,
          color: '#061b31',
          fontSize: 13,
          fontWeight: 400,
          paddingLeft: 4,
        }}
      >
        <BgColorsOutlined
          style={{ color: currentColor, transition: 'all 0.3s' }}
        />
        <span>品牌主题色彩</span>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: 12,
        }}
      >
        {THEME_COLOR_PRESETS.map((color) => {
          const active = currentColor === color;

          return (
            <button
              key={color}
              aria-label={`切换主题色为 ${color}`}
              onClick={() => applyThemeColor(color)}
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                outline: 'none',
                position: 'relative',
                transform: active ? 'scale(1.1)' : 'scale(1)',
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.transform = 'scale(1.15)';
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.transform = 'scale(1)';
              }}
              type="button"
            >
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 4,
                  background: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: active
                    ? `0 0 0 2px #fff, 0 0 0 4px ${color}40, 0 4px 12px ${color}60`
                    : `0 2px 6px ${color}20`,
                  transition: 'all 0.3s ease',
                }}
              >
                {active && (
                  <CheckOutlined
                    style={{ color: '#fff', fontSize: 12, fontWeight: 'bold' }}
                  />
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <Popover
      content={palette}
      open={open}
      placement="bottomRight"
      trigger="click"
      onOpenChange={setOpen}
      styles={{
        body: {
          borderRadius: 6,
          padding: 0,
          boxShadow:
            'rgba(50,50,93,0.25) 0px 30px 45px -30px, rgba(0,0,0,0.1) 0px 18px 36px -18px',
        },
      }}
    >
      <Tooltip title="品牌主题">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 36,
            width: 36,
            cursor: 'pointer',
            borderRadius: 6,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          className="header-action-item"
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = 'rgba(83,58,253,0.06)')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }
        >
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: 5,
              background: currentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 2px 8px ${currentColor}50`,
              transition: 'all 0.3s',
            }}
          >
            <BgColorsOutlined style={{ color: '#fff', fontSize: 11 }} />
          </div>
        </div>
      </Tooltip>
    </Popover>
  );
};

const HeaderActions = () => {
  return (
    <Space size={4} style={{ marginRight: 8 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
        }}
      >
        <Input
          placeholder="快速搜索..."
          prefix={<SearchOutlined style={{ color: '#8aa0b8', fontSize: 13 }} />}
          variant="filled"
          style={{
            borderRadius: 4,
            width: 160,
            height: 28,
            background: 'rgba(83,58,253,0.05)',
            border: 'none',
            fontSize: 12,
          }}
        />
      </div>
      <ThemeColorPicker />
      <Tooltip title="通知中心">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 36, // 缩小高度
            width: 36,
            cursor: 'pointer',
            borderRadius: 6,
            transition: 'all 0.3s',
          }}
          className="header-action-item"
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = 'rgba(83,58,253,0.06)')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }
        >
          <BellOutlined style={{ fontSize: 16, color: '#64748d' }} />
        </div>
      </Tooltip>
      <Tooltip title="帮助文档">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 36,
            width: 36,
            cursor: 'pointer',
            borderRadius: 6,
            transition: 'all 0.3s',
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = 'rgba(83,58,253,0.06)')
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = 'transparent')
          }
        >
          <QuestionCircleOutlined style={{ fontSize: 16, color: '#64748d' }} />
        </div>
      </Tooltip>
    </Space>
  );
};

export const antd: RuntimeAntdConfig = (memo) => {
  const themeColor = getStoredThemeColor();

  return {
    ...memo,
    theme: {
      ...memo.theme,
      ...buildAntdThemeConfig(themeColor),
    },
  };
};

type RuntimeMenuItem = MenuDataItem & {
  isUrl?: boolean;
  itemPath?: string;
};

const shouldRenderNestedMenuIcon = (item: MenuDataItem) => {
  return Boolean(
    item.icon && typeof item.level === 'number' && item.level >= 0,
  );
};

const renderMenuLabel = (item: MenuDataItem, defaultDom: React.ReactNode) => {
  if (!shouldRenderNestedMenuIcon(item)) {
    return defaultDom;
  }

  return (
    <span className="zhiht-menu-label">
      <span className="zhiht-menu-label-icon">{item.icon}</span>
      <span className="zhiht-menu-label-text">{item.name}</span>
    </span>
  );
};

export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const themeColor = resolveThemeColor(
    initialState?.settings?.colorPrimary || DEFAULT_THEME_COLOR,
  );

  return {
    menu: {
      params: {
        userId: initialState?.currentUser?.id,
      },
      request: async () => initialState?.menuData ?? [],
    },
    subMenuItemRender: (menuItemProps, defaultDom) =>
      renderMenuLabel(menuItemProps, defaultDom),
    menuItemRender: (menuItemProps, defaultDom) => {
      const item = menuItemProps as RuntimeMenuItem;
      const content = renderMenuLabel(item, defaultDom);
      const itemPath = item.itemPath || item.path;

      if (item.isUrl) {
        return (
          <a
            className="zhiht-menu-label-link"
            href={itemPath}
            rel="noreferrer"
            target={item.target || '_blank'}
          >
            {content}
          </a>
        );
      }

      if (item.path && history.location.pathname !== item.path) {
        return (
          <Link
            className="zhiht-menu-label-link"
            target={item.target}
            to={item.path.replace('/*', '')}
          >
            {content}
          </Link>
        );
      }

      return content;
    },
    avatarProps: initialState?.currentUser
      ? {
          src: initialState.currentUser.avatar,
          title: <AvatarName />,
          render: (_, avatarChildren) => (
            <AvatarDropdown>{avatarChildren}</AvatarDropdown>
          ),
        }
      : undefined,
    waterMarkProps: undefined,
    footerRender: () => <Footer />,
    links: [],
    actionsRender: () => [<HeaderActions key="actions" />],
    onPageChange: () => {
      const pathname = history.location.pathname;
      const isLoggedIn = Boolean(useAuthStore.getState().accessToken);

      if (!isLoggedIn && !isPublicPath(pathname)) {
        redirectToLogin();
      }
    },
    childrenRender: (children) => children,
    onCollapse: (collapsed) => {
      if (!setInitialState) {
        return;
      }

      setInitialState((previousState) => ({
        ...(previousState || {}),
        settings: {
          ...mergeThemeIntoSettings(previousState?.settings, themeColor),
          collapsed,
        },
      }));
    },
    ...buildLayoutSettings(themeColor),
    ...initialState?.settings,
  };
};
